import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Lozenge from "@atlaskit/lozenge";

import {
    Addon,
    AddonsContainer,
    learnMoreEventData
} from "@/components/wpl/pricing/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { AtlassianGuardMarkBrand } from "@/libraries/wpl/foundations/src/logos/atlassian-guard-mark-brand";
import { ConfluenceMarkBrand } from "@/libraries/wpl/foundations/src/logos/confluence-mark-brand";
const logoContainerCss = css({
    height: 60
});

const confluenceLogoCss = css({
    position: "relative",
    insetBlockStart: -16,
    insetInlineStart: -12
});

const lozengeContainerStyle = css({
    insetBlockEnd: 2,
    display: "inline",
    marginInlineStart: 4,
    position: "absolute"
});
export const ConfluenceAddons = () => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    return (
        <AddonsContainer
            heading={
                <FormattedMessage
                    id="ConfluencePricingPageTemplate.addons.sectionHeader"
                    defaultMessage="Power up your team on Confluence"
                />
            }
        >
            <Addon
                logo={
                    <div css={logoContainerCss}>
                        <AtlassianGuardMarkBrand
                            style={{
                                height: 60,
                                width: 60
                            }}
                        />
                    </div>
                }
                heading={intl.formatMessage(
                    {
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.heading",
                        defaultMessage:
                            "Enhance cloud security with <b>Atlassian Guard</b>"
                    },
                    {
                        b: (chunks: ReactNode) => <b>{chunks}</b>
                    }
                )}
                subheading={intl.formatMessage({
                    id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.subheading",
                    defaultMessage: "Priced separately, per unique user"
                })}
                features={[
                    intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.features.auth",
                        defaultMessage:
                            "Enforce SSO, 2FA, and mobile policies on managed and external users"
                    }),
                    intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.features.classificationAndDetection",
                        defaultMessage:
                            "Set classification levels and detect suspicious activity"
                    }),
                    intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.features.remediation",
                        defaultMessage:
                            "Respond with actionable remediation steps"
                    })
                ]}
                learnMore={{
                    text: intl.formatMessage({
                        id: "JiraSoftwarePricingPageTemplate.addons.atlassianGuard.learnMore",
                        defaultMessage: "Learn more"
                    }),
                    href: useLocalizedHref("/software/access"),
                    onClick: () =>
                        createAnalyticsEvent({
                            ...learnMoreEventData,
                            attributes: {
                                product: "confluence",
                                addon: "atlassianGuard"
                            }
                        }).fire()
                }}
            />

            <Addon
                logo={
                    <div css={logoContainerCss}>
                        <ConfluenceMarkBrand
                            css={confluenceLogoCss}
                            style={{
                                height: 80,
                                width: 80
                            }}
                        />
                    </div>
                }
                heading={intl.formatMessage(
                    {
                        id: "ConfluencePricingPageTemplate.addons.loom.heading",
                        defaultMessage:
                            "Collaborate with <b>Loom videos in Confluence</b><lozenge></lozenge>"
                    },
                    {
                        b: (chunks: ReactNode) => <b>{chunks}</b>,
                        lozenge: () => (
                            <div css={lozengeContainerStyle}>
                                <Lozenge appearance="inprogress">BETA</Lozenge>
                            </div>
                        )
                    }
                )}
                subheading={intl.formatMessage({
                    id: "ConfluencePricingPageTemplate.addons.loom.subheading",
                    defaultMessage:
                        "Free Loom business plan features during beta program"
                })}
                features={[
                    intl.formatMessage({
                        id: "ConfluencePricingPageTemplate.addons.loom.features.recordAndEmbed",
                        defaultMessage:
                            "Record and embed video resources directly in Confluence"
                    }),
                    intl.formatMessage({
                        id: "ConfluencePricingPageTemplate.addons.loom.features.comments",
                        defaultMessage: "Add video comments to pages"
                    }),
                    intl.formatMessage({
                        id: "ConfluencePricingPageTemplate.addons.loom.features.recordings",
                        defaultMessage:
                            "Search and insert existing Loom recordings in seconds"
                    })
                ]}
                learnMore={{
                    text: intl.formatMessage({
                        id: "ConfluencePricingPageTemplate.addons.loom.learnMore",
                        defaultMessage: "Join the waitlist"
                    }),
                    href: useLocalizedHref("/software/confluence/loom"),
                    onClick: () =>
                        createAnalyticsEvent({
                            ...learnMoreEventData,
                            attributes: {
                                product: "confluence",
                                addon: "loom"
                            }
                        }).fire()
                }}
            />
        </AddonsContainer>
    );
};
